/**
 * Timer V2 8G.1 — the explicit human ticket-completion client boundary.
 *
 * ============================================================================
 * WHY THIS EXISTS
 * ============================================================================
 * Completing a visit is TWO human assertions that must land as ONE fact:
 *
 *   1. "this work is finished"      (CORE-AL)
 *   2. "the car is now HERE"        (CORE-AO)
 *
 * They cannot be sequenced client-side. `tickets_lifecycle_terminal_state` is
 * an IMMEDIATE check forbidding a terminal status together with a bay, so
 * "complete, then park" is rejected at the first statement — measured, 23514.
 * The server therefore commits completion and placement in one statement, and
 * this module's only job is to carry the human's two assertions to it.
 *
 * ============================================================================
 * THE LOCATION IS ASSERTED, NEVER INFERRED
 * ============================================================================
 * A missing spot id does NOT mean "general lot". CORE-AO deliberately permits
 * lower precision — a general area rather than a numbered space — but that is
 * something a person CHOOSES, not something the absence of input implies. So
 * the kind is explicit and the server refuses `LOCATION_REQUIRED` rather than
 * defaulting.
 */
import { supabase } from '@/integrations/supabase/client';

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;

// RECEIVER-PRESERVING. `SupabaseClient.rpc` is a prototype method whose body is
// `return this.rest.rpc(...)`, so detaching it —
//
//     const rpc = supabase.rpc as unknown as Rpc;   // <- what this used to be
//
// — drops the receiver and throws `TypeError: Cannot read properties of
// undefined (reading 'rest')` before the request is ever sent. That is not
// theoretical: it is what production returned for the timer state read and the
// ticket delete, captured with http_status = null because nothing was sent.
//
// The wrapper below calls the method ON the client, so the receiver survives.
// Every call site is unchanged.
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

/** Where the finished vehicle went. Both are deliberate human answers. */
export type CompletionLocationKind = 'PARKING_SPOT' | 'GENERAL_LOT';

export type CompleteTicketOutcome =
  /** The visit is closed and the vehicle is placed. */
  | 'COMPLETED'
  /** Somebody already completed it; the location and timestamp are untouched. */
  | 'ALREADY_COMPLETED'
  /** No location was asserted. Nothing was completed. */
  | 'LOCATION_REQUIRED'
  /** The chosen space is taken, inactive, or not this shop's. Nothing completed. */
  | 'LOCATION_CONFLICT'
  /** A technician paused work here and intends to continue. */
  | 'PAUSED_WORK'
  /** Open jobs, running timers, unreviewed time, or an open split sibling. */
  | 'NOT_ELIGIBLE'
  /** Unknown, another tenant's, or one this caller may not complete — one answer. */
  | 'TICKET_NOT_AVAILABLE'
  /** A diagnostic or inspection job has no recorded outcome. */
  | 'DIAGNOSTIC_REQUIRED'
  /** A comeback with no recorded resolution. */
  | 'COMEBACK_REQUIRED'
  /** Already Cancelled or Deleted. Reopening is a different authority. */
  | 'TICKET_NOT_COMPLETABLE'
  /** The visit was split or merged between reading the group and locking it. */
  | 'GROUP_CHANGED_RETRY'
  /** Core-100 direction 9: a Detail Specialist may end detail-only visits; this one has other work. */
  | 'NOT_DETAIL_ONLY'
  /** The call produced no server answer. This does NOT mean nothing committed. */
  | 'SUBMIT_FAILED';

export interface CompleteTicketResult {
  outcome: CompleteTicketOutcome;
  ticketId: string;
  reasons: string[];
  note: string | null;
}

/** Whether the visit is closed, whoever closed it. A retry after a lost
 *  response returning ALREADY_COMPLETED is a success, not an error. */
export function isCompletionSettled(outcome: CompleteTicketOutcome): boolean {
  return outcome === 'COMPLETED' || outcome === 'ALREADY_COMPLETED';
}

/**
 * Complete a ticket and place the vehicle, atomically.
 *
 * The browser sends only the ticket, the location kind and (for a numbered
 * space) the spot. Shop, performer, actor and timestamp are all derived
 * server-side — a tampered client cannot name who did the work or when.
 */
export async function completeTicketWithLocation(
  ticketId: string,
  /**
   * `null` states an unanswered question DELIBERATELY. EVE uses it: a spoken
   * "complete this ticket" is a real completion intent that carries no
   * location, and the server answers LOCATION_REQUIRED without touching the
   * row. Passing null is how a caller asks "may I?", never a shorthand for
   * the general lot.
   */
  locationKind: CompletionLocationKind | null,
  parkingSpotId?: string | null,
): Promise<CompleteTicketResult> {
  const { data, error } = await rpc('complete_ticket_with_location', {
    p_ticket_id: ticketId,
    p_location_kind: locationKind,
    p_parking_spot_id: locationKind === 'PARKING_SPOT' ? (parkingSpotId ?? null) : null,
  });

  if (error) {
    console.warn('[completeTicketWithLocation] failed', error);
    return { outcome: 'SUBMIT_FAILED', ticketId, reasons: [], note: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const known: CompleteTicketOutcome[] = [
    'COMPLETED', 'ALREADY_COMPLETED', 'LOCATION_REQUIRED', 'LOCATION_CONFLICT',
    'PAUSED_WORK', 'NOT_ELIGIBLE', 'TICKET_NOT_AVAILABLE',
    'DIAGNOSTIC_REQUIRED', 'COMEBACK_REQUIRED', 'TICKET_NOT_COMPLETABLE',
    'GROUP_CHANGED_RETRY', 'NOT_DETAIL_ONLY',
  ];
  const outcome = known.includes(r.outcome as CompleteTicketOutcome)
    ? (r.outcome as CompleteTicketOutcome)
    : 'SUBMIT_FAILED';

  const result: CompleteTicketResult = {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    reasons: Array.isArray(r.reasons) ? (r.reasons as string[]) : [],
    note: (r.note as string) ?? (r.reason as string) ?? null,
  };

  // ── CORE-AN: efficiency learning is NOT called from here ──────────────
  //
  // It used to be, and that was the whole of the delivery mechanism. This
  // module is a browser. Completion commits server-side, and the learning
  // call was a SECOND round trip from a device that may be closed, offline,
  // backgrounded or simply refused a slow network — and its failure was a
  // console.warn, deliberately swallowed so a learning problem could not turn
  // a completed visit into a failed one for the person who just finished the
  // car.
  //
  // That was the right call to make about THIS request and the wrong place to
  // make the decision from, because nothing else ever ran. There was no cron
  // job, no trigger and no second call site, and the obvious repair — complete
  // again — is worse than useless: a repeat answers ALREADY_COMPLETED and
  // returns before it could learn. Measured, a completion whose learning call
  // never fired was unrecoverable: efficiency unchanged, no ledger row, no
  // path back.
  //
  // Delivery is now `core_an_sweep_efficiency_learning`, on the server, every
  // five minutes, from the activation watermark forward. And the browser could
  // not call the authority now even if this code asked it to: EXECUTE is
  // revoked from anon and authenticated by name, because the value decides who
  // gets assigned work and the people it rates were able to write it.

  return result;
}

/** Human-readable explanation of a refusal. */
export function describeCompletionRefusal(result: CompleteTicketResult): string {
  switch (result.outcome) {
    case 'PAUSED_WORK':
      return 'A technician has paused work on this ticket and intends to continue.';
    case 'LOCATION_REQUIRED':
      return 'Choose where the vehicle went before completing.';
    case 'LOCATION_CONFLICT':
      return result.note ?? 'That space is not available.';
    case 'NOT_ELIGIBLE':
      return result.reasons.length ? result.reasons.join(' · ') : 'Work remains on this ticket.';
    case 'TICKET_NOT_AVAILABLE':
      return 'This ticket is not available.';
    case 'DIAGNOSTIC_REQUIRED':
      return result.reasons.length
        ? result.reasons.join(' · ')
        : 'Record the diagnostic or inspection outcome before completing.';
    case 'COMEBACK_REQUIRED':
      return 'Record why this vehicle came back before completing.';
    case 'TICKET_NOT_COMPLETABLE':
      return result.reasons.length
        ? result.reasons.join(' · ')
        : 'This visit has already ended and cannot be completed.';
    case 'GROUP_CHANGED_RETRY':
      return 'This visit was split or merged just now. Try again.';
    case 'NOT_DETAIL_ONLY':
      return 'This visit includes work other than detailing, so the front office finishes it.';
    case 'SUBMIT_FAILED':
      return 'The result could not be confirmed. Try again.';
    default:
      return '';
  }
}
